import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong, faKey } from '@fortawesome/free-solid-svg-icons';
import { PatternFormat } from 'react-number-format';
import { Col, Row, Spinner } from 'react-bootstrap';
import { Helpers } from '../../../utils/helpers';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { Container } from '../../../components/ui/Container/Container';
import styles from './UserEditor.module.css';
import ModalDialog from '../../../components/ui/ModalDialog/ModalDialog';
import { SetPasswordModal } from '../../../components/user/SetPasswordModal/SetPasswordModal';
import useUserEditor from './useUserEditor';
import { Permissions } from '../../../utils/constants/permissions';

interface IUserEditorProps {
  userId?: string;
}

export const UserEditor = ({ userId = '' }: IUserEditorProps) => {
  const manager = useUserEditor(userId);
  const ToastContainer = manager.ToastContainer;

  if (!userId) return <></>;

  return (
    <>
      <ToastContainer />
      <ModalDialog
        showDialog={manager.showUnsavedChangesDialog}
        heading="Unsaved Changes"
        message="You have unsaved changes. Do you want discard your changes?"
        okCallback={manager.discardUnsavedChanges}
        okButtonText="Yes, discard changes."
        cancelCallback={manager.closeUnsavedChangesDialog}
        cancelButtonText="No, I want to keep editing."
      />
      <SetPasswordModal
        visible={manager.showSetPasswordModal}
        userId={manager.user.id}
        onSuccess={manager.onPasswordSetSuccess}
        onClose={manager.closeSetPasswordModal}
      />
      <LoadingContainer isLoading={manager.loadingUser}>
        <div className="mb-2">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              manager.back();
            }}>
            <FontAwesomeIcon icon={faArrowLeftLong} className="me-1" />
            Return to user list
          </a>
        </div>
        <Container className="page-container">
          <h3>
            {Helpers.isNullOrWhiteSpace(manager.user.id) || manager.user.id === 'new'
              ? 'Create'
              : 'Edit'}{' '}
            User
          </h3>
          <Row>
            <Col lg={10} md={8} sm={12}>
              <div className="row">
                <div className="col-lg-6 col-md-12">
                  <div className="mb-3">
                    <label htmlFor="emailAddress">Email Address</label>
                    <div className="d-flex">
                      <input
                        type="email"
                        id="emailAddress"
                        maxLength={65}
                        className={`form-control ${
                          manager.fieldErrors.includes('overview:emailAddress') ? 'is-invalid' : ''
                        }`}
                        value={manager.user.emailAddress}
                        onChange={(e: React.FormEvent<HTMLInputElement>) => {
                          manager.updateValue('emailAddress', e.currentTarget.value);
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-12">
                  <div className="mb-3">
                    <label htmlFor="cellPhone">Cell Phone (optional)</label>
                    <PatternFormat
                      id="cellPhone"
                      maxLength={15}
                      className={`form-control ${
                        manager.fieldErrors.includes('overview:cellPhone') ? 'is-invalid' : ''
                      }`}
                      format="(###) ### ####"
                      mask="_"
                      value={manager.user.cellPhone}
                      onChange={(e: React.FormEvent<HTMLInputElement>) => {
                        manager.updateValue('cellPhone', e.currentTarget.value);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-6 col-md-12">
                  <div className="mb-3">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      maxLength={65}
                      className={`form-control ${
                        manager.fieldErrors.includes('overview:firstName') ? 'is-invalid' : ''
                      }`}
                      value={manager.user.firstName}
                      onChange={(e: React.FormEvent<HTMLInputElement>) => {
                        manager.updateValue('firstName', e.currentTarget.value);
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-12">
                  <div className="mb-3">
                    <label htmlFor="firstName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      maxLength={65}
                      className={`form-control ${
                        manager.fieldErrors.includes('overview:lastName') ? 'is-invalid' : ''
                      }`}
                      value={manager.user.lastName}
                      onChange={(e: React.FormEvent<HTMLInputElement>) => {
                        manager.updateValue('lastName', e.currentTarget.value);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label>Permissions</label>
                <div className="border rounded p-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="perm-sysadmin"
                      checked={(manager.user.permissions || []).includes(Permissions.SYSADMIN)}
                      onChange={() => manager.togglePermission(Permissions.SYSADMIN)}
                    />
                    <label className="form-check-label" htmlFor="perm-sysadmin">
                      System Administrator
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="perm-member-update"
                      checked={(manager.user.permissions || []).includes(Permissions.MEMBER_UPDATE)}
                      onChange={() => manager.togglePermission(Permissions.MEMBER_UPDATE)}
                    />
                    <label className="form-check-label" htmlFor="perm-member-update">
                      Update Members
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="perm-member-readonly"
                      checked={(manager.user.permissions || []).includes(
                        Permissions.MEMBER_READ_ONLY
                      )}
                      onChange={() => manager.togglePermission(Permissions.MEMBER_READ_ONLY)}
                    />
                    <label className="form-check-label" htmlFor="perm-member-readonly">
                      Read-only Access
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="perm-member-delete"
                      checked={(manager.user.permissions || []).includes(Permissions.MEMBER_DELETE)}
                      onChange={() => manager.togglePermission(Permissions.MEMBER_DELETE)}
                    />
                    <label className="form-check-label" htmlFor="perm-member-delete">
                      Delete Members
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="perm-workhours-update"
                      checked={(manager.user.permissions || []).includes(
                        Permissions.WORKHOURS_UPDATE
                      )}
                      onChange={() => manager.togglePermission(Permissions.WORKHOURS_UPDATE)}
                    />
                    <label className="form-check-label" htmlFor="perm-workhours-update">
                      Update Work Hours
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="perm-gen-invoice"
                      checked={(manager.user.permissions || []).includes(Permissions.GEN_INVOICE)}
                      onChange={() => manager.togglePermission(Permissions.GEN_INVOICE)}
                    />
                    <label className="form-check-label" htmlFor="perm-gen-invoice">
                      Generate Invoices
                    </label>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={2} md={4} sm={12}>
              <div className={styles.fixedMenu}>
                <div className="d-grid gap-2 mb-4">
                  <button
                    className="btn btn-primary"
                    onClick={manager.saveChanges}
                    disabled={manager.isSaving}>
                    Save Changes
                    {manager.isSaving && <Spinner size="sm" animation="border" className="ms-2" />}
                  </button>
                  {!(Helpers.isNullOrWhiteSpace(manager.user.id) || manager.user.id === 'new') && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={manager.openSetPasswordModal}
                      disabled={manager.isSaving}>
                      <FontAwesomeIcon icon={faKey} className="me-1" />
                      Set Password
                    </button>
                  )}
                </div>
                <div className={styles.dateContainer}>
                  <strong>Created</strong>
                  <div className={styles.dateDisplay}>
                    {Helpers.toShortDateTime(manager.user.dateCreatedUtc, 'Never')}
                    <div className={styles.createUpdateByLabel}>
                      by {Helpers.valueOrDefault(manager.user.createdBy, 'Unknown')}
                    </div>
                  </div>
                </div>
                <div className={styles.dateContainer}>
                  <strong>Last Updated</strong>
                  <div className={styles.dateDisplay}>
                    {Helpers.toShortDateTime(manager.user.lastModifiedDateUtc, 'Never')}
                    <div className={styles.createUpdateByLabel}>
                      by {Helpers.valueOrDefault(manager.user.lastModifiedBy, 'Unknown')}
                    </div>
                  </div>
                </div>
                {!(Helpers.isNullOrWhiteSpace(manager.user.id) || manager.user.id === 'new') && (
                  <div className="d-grid gap-2 mt-3">
                    <button
                      className="btn btn-danger"
                      onClick={() => manager.deleteUser()}
                      disabled={manager.isSaving}>
                      Remove App Access
                      {manager.isSaving && (
                        <Spinner size="sm" animation="border" className="ms-2" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </LoadingContainer>
    </>
  );
};
